import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Upload, Download, Search, Loader2, Database, ListChecks, History } from 'lucide-react';

const enconOrange = '#ee7203';

const PhocasCustomerDatabase = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [autoAddSettings, setAutoAddSettings] = useState([]);
  const [savingSector, setSavingSector] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const fileInputRef = useRef(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/phocas-customers/stats`);
      setStats(res.data);
    } catch (e) {
      toast.error('Failed to load Phocas Customer Database stats');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAutoAddSettings = useCallback(async () => {
    try {
      const res = await api.get(`${API}/phocas-customers/auto-add-settings`);
      setAutoAddSettings(res.data.settings || []);
    } catch (e) {
      toast.error('Failed to load auto-add settings');
    }
  }, []);

  const loadAuditLog = useCallback(async () => {
    try {
      const res = await api.get(`${API}/phocas-customers/auto-add-log?limit=30`);
      setAuditLog(res.data.log || []);
    } catch (e) {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadAutoAddSettings();
    loadAuditLog();
  }, [loadStats, loadAutoAddSettings, loadAuditLog]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      toast.error('Please upload an .xlsx file');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`${API}/phocas-customers/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data;
      const sectorSummary = Object.entries(d.per_sector || {}).map(([k, v]) => `${k}: ${v}`).join(', ');
      toast.success(`Imported ${d.total_imported.toLocaleString()} rows (${sectorSummary})`);
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to import Phocas customers');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`${API}/phocas-customers/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `phocas_customer_database_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to download Phocas customer database');
    } finally {
      setDownloading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return setSearchResults([]);
    setSearching(true);
    try {
      const res = await api.get(`${API}/phocas-customers/search`, { params: { q: searchQuery, limit: 50 } });
      setSearchResults(res.data.results || []);
    } catch (e) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const [nicknameDrafts, setNicknameDrafts] = useState({}); // account_code -> in-progress text
  const [savingNickname, setSavingNickname] = useState(null);

  const saveNickname = async (accountCode) => {
    const draft = nicknameDrafts[accountCode];
    if (draft === undefined) return; // unchanged, nothing to save
    setSavingNickname(accountCode);
    try {
      await api.put(`${API}/phocas-customers/${encodeURIComponent(accountCode)}`, { secondary_nickname: draft });
      setSearchResults(prev => prev.map(c => c.account_code === accountCode ? { ...c, secondary_nickname: draft.trim() || null } : c));
      setNicknameDrafts(prev => { const next = { ...prev }; delete next[accountCode]; return next; });
      toast.success('Secondary nickname saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save nickname');
    } finally {
      setSavingNickname(null);
    }
  };

  const updateSectorField = (sector, field, value) => {
    setAutoAddSettings(prev => prev.map(s => s.market_sector === sector ? { ...s, [field]: value } : s));
  };

  const saveSectorSettings = async (sector) => {
    const s = autoAddSettings.find(x => x.market_sector === sector);
    if (!s) return;
    setSavingSector(sector);
    try {
      await api.put(`${API}/phocas-customers/auto-add-settings/${encodeURIComponent(sector)}`, {
        market_sector: sector,
        auto_add_enabled: !!s.auto_add_enabled,
        order_value_threshold: parseFloat(s.order_value_threshold) || 0,
        follow_up_working_days: parseInt(s.follow_up_working_days, 10) || 0,
      });
      toast.success(`${sector} auto-add settings saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSector(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="phocas-customer-database-page">
      {/* Import + Stats */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Database className="w-6 h-6" style={{ color: enconOrange }} />
              Phocas Customer Database
            </span>
            <div className="flex gap-2">
              <Button
                onClick={handleDownload}
                disabled={downloading || !stats?.total}
                variant="outline"
                data-testid="phocas-download-btn"
              >
                {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download
              </Button>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{ backgroundColor: enconOrange }}
                className="text-white"
                data-testid="phocas-import-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Import Workbook
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="phocas-import-file-input"
              />
            </div>
          </CardTitle>
          <CardDescription>
            Import the Phocas customer export (one sheet per market sector: Nevill Long, Structural,
            Merchants, Technical, Other). Re-uploading safely refreshes existing records by account code —
            any Secondary Nicknames you've set below are preserved across re-imports. Search below to add
            a Secondary Nickname (a colloquial/abbreviated name, e.g. "TOPS INTERIORS") so quotes parsed
            under that name still match the correct account code for CRM.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg text-center col-span-1" data-testid="phocas-total-count">
                  <p className="text-2xl font-bold" style={{ color: enconOrange }}>{(stats?.total || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Total Customers</p>
                </div>
                {(stats?.market_sectors || []).map(sector => (
                  <div key={sector} className="bg-gray-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-gray-700">{(stats?.by_sector?.[sector] || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{sector}</p>
                  </div>
                ))}
              </div>
              {stats?.last_imported_at && (
                <p className="text-xs text-gray-400 mb-4">Last imported: {new Date(stats.last_imported_at).toLocaleString()}</p>
              )}

              {/* Search */}
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="Search by account code, customer name, or secondary nickname..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="flex-1"
                  data-testid="phocas-search-input"
                />
                <Button onClick={handleSearch} disabled={searching} variant="outline" data-testid="phocas-search-btn">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>

              {searchResults.length > 0 && (
                <div className="rounded-lg border border-gray-200 overflow-hidden max-h-80 overflow-y-auto" data-testid="phocas-search-results">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-gray-500">Account Code</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-500">Customer Name</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-500">Secondary Nickname</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-500">Sector</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-500">Total Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map(c => (
                        <tr key={c.account_code} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono text-gray-700">{c.account_code}</td>
                          <td className="px-3 py-2 text-gray-800">{c.customer_name}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <Input
                                value={nicknameDrafts[c.account_code] !== undefined ? nicknameDrafts[c.account_code] : (c.secondary_nickname || '')}
                                onChange={(e) => setNicknameDrafts(prev => ({ ...prev, [c.account_code]: e.target.value }))}
                                onKeyDown={(e) => e.key === 'Enter' && saveNickname(c.account_code)}
                                placeholder="e.g. TOPS INTERIORS"
                                className="h-7 text-xs min-w-[140px]"
                                data-testid={`phocas-nickname-input-${c.account_code}`}
                              />
                              {nicknameDrafts[c.account_code] !== undefined && (
                                <Button
                                  size="sm"
                                  onClick={() => saveNickname(c.account_code)}
                                  disabled={savingNickname === c.account_code}
                                  style={{ backgroundColor: enconOrange }}
                                  className="h-7 px-2 text-white text-xs"
                                  data-testid={`phocas-nickname-save-${c.account_code}`}
                                >
                                  {savingNickname === c.account_code ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                                </Button>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-gray-500">{c.market_sector}</td>
                          <td className="px-3 py-2 text-right text-gray-700">
                            {c.total_value != null ? `£${Number(c.total_value).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Section 3 — Market Sector Auto-Add to CRM settings */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListChecks className="w-5 h-5" style={{ color: enconOrange }} />
            Auto-Add to CRM (per Market Sector)
          </CardTitle>
          <CardDescription>
            When enabled, quotes parsed above a sector's threshold are automatically added to Zoho CRM
            with no manual click — using account-code-first matching. All sectors default OFF; turn a
            sector on only once you're confident in the matching for that sector.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {autoAddSettings.map(s => (
            <div key={s.market_sector} className="rounded-xl border border-gray-200 p-4 flex flex-wrap items-end gap-4" data-testid={`auto-add-sector-row-${s.market_sector.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="min-w-[140px]">
                <p className="font-semibold text-gray-800">{s.market_sector}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Switch
                    checked={!!s.auto_add_enabled}
                    onCheckedChange={(v) => updateSectorField(s.market_sector, 'auto_add_enabled', v)}
                    data-testid={`auto-add-toggle-${s.market_sector.replace(/\s+/g, '-').toLowerCase()}`}
                  />
                  <span className="text-xs text-gray-500">{s.auto_add_enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Order Value Threshold (£)</Label>
                <Input
                  type="number"
                  min="0"
                  step="50"
                  value={s.order_value_threshold}
                  onChange={(e) => updateSectorField(s.market_sector, 'order_value_threshold', e.target.value)}
                  className="w-36"
                  data-testid={`auto-add-threshold-${s.market_sector.replace(/\s+/g, '-').toLowerCase()}`}
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Follow-up (working days)</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={s.follow_up_working_days}
                  onChange={(e) => updateSectorField(s.market_sector, 'follow_up_working_days', e.target.value)}
                  className="w-28"
                  data-testid={`auto-add-followup-${s.market_sector.replace(/\s+/g, '-').toLowerCase()}`}
                />
              </div>
              <Button
                size="sm"
                onClick={() => saveSectorSettings(s.market_sector)}
                disabled={savingSector === s.market_sector}
                style={{ backgroundColor: enconOrange }}
                className="text-white"
                data-testid={`auto-add-save-${s.market_sector.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {savingSector === s.market_sector ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
              {s.updated_at && (
                <span className="text-xs text-gray-400">Updated {new Date(s.updated_at).toLocaleDateString()} by {s.updated_by || '—'}</span>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" style={{ color: enconOrange }} />
            Auto-Add Activity Log
          </CardTitle>
          <CardDescription>Every auto-add decision (fired, skipped, or failed) is logged here so you can see exactly why.</CardDescription>
        </CardHeader>
        <CardContent>
          {auditLog.length === 0 ? (
            <p className="text-sm text-gray-400" data-testid="phocas-audit-log-empty">No auto-add activity yet.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="phocas-audit-log-list">
              {auditLog.map((row, i) => (
                <div key={i} className="text-sm border-b border-gray-100 pb-2 flex justify-between gap-3">
                  <div>
                    <span className={`font-semibold ${row.outcome === 'created' ? 'text-green-600' : row.outcome === 'skipped' ? 'text-gray-500' : 'text-red-500'}`}>
                      {(row.outcome || '').toUpperCase()}
                    </span>{' '}
                    <span className="text-gray-700">{row.customer_name}</span>{' '}
                    <span className="text-gray-400">({row.market_sector || 'unknown sector'})</span>
                    <p className="text-xs text-gray-400">{row.reason}</p>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {row.created_at ? new Date(row.created_at).toLocaleString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PhocasCustomerDatabase;
